import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApplicationService } from '../../services/api/userApplicationService';

export function useApplicationsQuery(userId) {
  return useQuery({
    queryKey: ['applications', userId],
    queryFn: () => userApplicationService.getUserApplications(),
    enabled: !!userId,
    // Retry on 401 since Clerk token might not be ready on first attempt
    retry: (failureCount, error) => {
      // Retry up to 3 times for 401 errors (auth timing issue)
      if (error?.response?.status === 401 && failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000),
  });
}

export function useCreateApplicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ trademarkId, applicationName }) =>
      userApplicationService.createApplication(trademarkId, applicationName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['applications', variables.userId] });
    },
  });
}

export function useUpdateApplicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      userApplicationService.updateApplication(applicationId, data),
    onSuccess: (updated) => {
      if (!updated?.id) return;
      // Merge the updated application into every cached applications list so
      // currentApplication reflects the save without a refetch (autosave runs
      // on a debounce — invalidating would refetch on every pause in typing)
      queryClient.setQueriesData({ queryKey: ['applications'] }, (old) =>
        Array.isArray(old)
          ? old.map((app) => (app.id === updated.id ? { ...app, ...updated } : app))
          : old
      );
    },
  });
}

export function useCancelApplicationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId }) =>
      userApplicationService.cancelApplication(applicationId),
    onSuccess: () => {
      // Invalidate all application queries to force refresh
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });
}
